import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import CartContext from '../../context/CartContext';
import AuthContext from '../../context/AuthContext';

const Checkout = () => {
  const { cart, clearCart } = useContext(CartContext);
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    paymentMethod: 'payhere'
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSummary, setOrderSummary] = useState({
    subtotal: 0,
    tax: 0,
    shipping: 0,
    total: 0
  });
  
  // Redirect if cart is empty or user is not authenticated
  useEffect(() => {
    if (cart.length === 0) {
      navigate('/cart');
    }
    
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout' } });
    }
  }, [cart, isAuthenticated, navigate]);
  
  // Pre-fill user data if available
  useEffect(() => {
    if (user) {
      const nameParts = user.name.split(' ');
      setFormData(prev => ({
        ...prev,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);
  
  // Calculate order summary
  useEffect(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = subtotal * 0.1; // 10% tax
    
    // Calculate shipping based on product settings
    let shipping = 0;
    
    // Check if ALL items have free shipping
    const allItemsHaveFreeShipping = cart.every(item => item.freeShipping === true);
    
    if (allItemsHaveFreeShipping) {
      // If all items have free shipping, shipping is always 0
      shipping = 0;
    } else if (subtotal > 100) {
      // If subtotal is over $100, provide free shipping regardless of product settings
      shipping = 0;
    } else {
      // Some items don't have free shipping and order is under $100
      // Find items that don't have free shipping
      const nonFreeShippingItems = cart.filter(item => !item.freeShipping);
      
      if (nonFreeShippingItems.length > 0) {
        // Extract shipping fees, defaulting to 5.99 if not present
        const shippingFees = nonFreeShippingItems.map(item => {
          return item.shippingFee !== undefined ? parseFloat(item.shippingFee) : 0.00;
        });
        
        // Find the highest shipping fee
        shipping = Math.max(...shippingFees);
      }
    }
    
    setOrderSummary({
      subtotal,
      tax,
      shipping,
      total: subtotal + tax + shipping
    });
  }, [cart]);
  
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };
  
  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phone', 
      'address', 'city', 'state', 'postalCode', 'country'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
      }
    });
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (formData.phone && !/^\+?[1-9]\d{9,14}$/.test(formData.phone)) {
      errors.phone = 'Phone number is invalid';
    }
    
    if (formData.postalCode && !/^[a-zA-Z0-9]{3,10}$/.test(formData.postalCode)) {
      errors.postalCode = 'Postal code is invalid';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create order
      const orderData = {
        products: cart.map(item => ({
          product: item._id,
          quantity: item.quantity
        })),
        shippingAddress: {
          street: formData.address,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country
        },
        paymentMethod: formData.paymentMethod
      };
      
      // Create the order
      const res = await api.post('/api/orders', orderData);
      
      // Store order ID in session storage for reference
      sessionStorage.setItem('lastOrderId', res.data._id);
      
      if (formData.paymentMethod === 'payhere') {
        // PayHere Integration - Redirect to actual PayHere gateway
        // Clear cart before redirecting
        clearCart();
        
        // Create a form to submit to PayHere
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = 'https://sandbox.payhere.lk/pay/checkout'; // PayHere sandbox URL
        form.style.display = 'none';
        
        // Required PayHere parameters
        const params = {
          merchant_id: '1219470',  // Replace with your actual merchant ID
          return_url: window.location.origin + '/payment/success',
          cancel_url: window.location.origin + '/payment/cancel',
          notify_url: 'https://your-backend-api.com/api/payment/notify',
          order_id: res.data._id,
          items: 'Order #' + res.data._id,
          amount: orderSummary.total.toString(),
          currency: 'USD',
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          delivery_address: formData.address,
          delivery_city: formData.city,
          delivery_country: formData.country
        };
        
        // Create hidden input fields
        Object.keys(params).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        });
        
        // Add form to body and submit
        document.body.appendChild(form);
        console.log('Submitting form to PayHere gateway...');
        form.submit();
        
      } else if (formData.paymentMethod === '2checkout') {
        // 2Checkout Integration - Redirect to actual 2Checkout gateway
        console.log('Initializing 2Checkout payment gateway...');
        
        try {
          // Clear cart before redirecting
          clearCart();
          
          // Create a form to submit to 2Checkout
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = 'https://secure.2checkout.com/checkout/purchase'; // Updated 2Checkout URL
          form.target = '_self'; // Ensure it opens in the same window
          form.style.display = 'none';
          
          // Required 2Checkout parameters
          const params = {
            sid: '901417094',  // Replace with your actual seller ID
            mode: '2CO',
            merchant_order_id: res.data._id,
            return_url: window.location.origin + '/payment/success',
            x_receipt_link_url: window.location.origin + '/payment/success',
            currency_code: 'USD',
            li_0_type: 'product',
            li_0_name: 'Order #' + res.data._id,
            li_0_price: orderSummary.total.toString(),
            li_0_quantity: '1',
            card_holder_name: `${formData.firstName} ${formData.lastName}`,
            street_address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.postalCode,
            country: formData.country,
            email: formData.email,
            phone: formData.phone
          };
          
          console.log('2Checkout params:', params);
          
          // Create hidden input fields
          Object.keys(params).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = params[key];
            form.appendChild(input);
          });
          
          // Add form to body
          document.body.appendChild(form);
          console.log('2Checkout form created, submitting...');
          
          // Submit the form after a small delay to ensure it's properly added to DOM
          setTimeout(() => {
            try {
              form.submit();
              console.log('2Checkout form submitted');
            } catch (submitError) {
              console.error('Error submitting 2Checkout form:', submitError);
              
              // Fallback: direct redirect
              const url = 'https://secure.2checkout.com/checkout/purchase?' + 
                Object.keys(params).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`).join('&');
              
              console.log('Fallback: redirecting to 2Checkout via window.location');
              window.location.href = url;
            }
          }, 100);          
        } catch (error) {
          console.error('Error setting up 2Checkout payment:', error);
          setFormErrors({ submit: 'Failed to initialize 2Checkout payment gateway. Please try again.' });
          setIsSubmitting(false);
        }
      } else {
        // Cash on Delivery - no payment gateway needed
        clearCart();
        navigate('/payment/success', { 
          state: { 
            orderId: res.data._id,
            paymentMethod: 'cod' 
          } 
        });
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setFormErrors({ submit: err.response?.data?.msg || 'Failed to process order' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">Checkout</h1>
      
      {formErrors.submit && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {formErrors.submit}
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Checkout Form */}
        <div className="lg:w-2/3">
          <form onSubmit={handleSubmit} className="neumorph p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-6">Shipping Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.firstName ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.firstName}</p>
                )}
              </div>
              
              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.lastName ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.lastName}</p>
                )}
              </div>
              
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.email ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>
              
              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.phone ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.phone && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                )}
              </div>
            </div>
            
            {/* Address */}
            <div className="mb-4">
              <label htmlFor="address" className="block text-gray-700 mb-2">Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                  formErrors.address ? 'border border-red-500' : ''
                }`}
              />
              {formErrors.address && (
                <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* City */}
              <div>
                <label htmlFor="city" className="block text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.city ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.city && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>
                )}
              </div>
              
              {/* State/Province */}
              <div>
                <label htmlFor="state" className="block text-gray-700 mb-2">State/Province</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.state ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.state && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.state}</p>
                )}
              </div>
              
              {/* Postal Code */}
              <div>
                <label htmlFor="postalCode" className="block text-gray-700 mb-2">Postal Code</label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.postalCode ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.postalCode && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.postalCode}</p>
                )}
              </div>
              
              {/* Country */}
              <div>
                <label htmlFor="country" className="block text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.country ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.country && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.country}</p>
                )}
              </div>
            </div>
            
            {/* Payment Method */}
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-4">Payment Method</h2>
              
              <div className="space-y-3">
                <label className="flex items-center neumorph p-4 rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="payhere"
                    checked={formData.paymentMethod === 'payhere'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="ml-2">PayHere</span>
                </label>
                
                <label className="flex items-center neumorph p-4 rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="2checkout"
                    checked={formData.paymentMethod === '2checkout'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="ml-2">2Checkout</span>
                </label>
                
                <label className="flex items-center neumorph p-4 rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cod"
                    checked={formData.paymentMethod === 'cod'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="ml-2">Cash on Delivery</span>
                </label>
              </div>
            </div>
            
            <button
              type="submit"
              className="neumorph-btn w-full py-3 font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Place Order'}
            </button>
          </form>
        </div>
        
        {/* Order Summary */}
        <div className="lg:w-1/3">
          <div className="neumorph p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            
            <div className="mb-6">
              {cart.map(item => (
                <div key={item._id} className="flex justify-between py-2 border-b last:border-b-0">
                  <div className="flex items-center">
                    <div className="neumorph p-1 rounded-lg mr-3">
                      <img 
                        src={item.imageUrl} 
                        alt={item.name} 
                        className="w-10 h-10 object-cover rounded-lg"
                      />
                    </div>
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                    </div>
                  </div>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>${orderSummary.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax (10%)</span>
                <span>${orderSummary.tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span>{orderSummary.shipping === 0 ? 'Free' : `$${orderSummary.shipping.toFixed(2)}`}</span>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>${orderSummary.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
